import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
    Lock, Tv, Users, Eye, Building2, DoorOpen, FileSignature,
    ShieldCheck, Check, X, Copy, ArrowRight, Landmark, PenLine
} from 'lucide-react';

/* ---------------------------------------------------------------
   All figures in ONE place with a retrieval date so nothing drifts.
   Re-verify before every send. House law: verify before you alarm.
----------------------------------------------------------------*/
const RETRIEVED = '18 August 2026';
const D = {
    trp: '2.0 to 2.5',
    subs: '14,900',
    subsNum: 14900,
    hostViews: '210,000',
    murmuViews: '68,000',
    motherViews: '31,000',
    partyLow: '776',
    partyHigh: '2,400',
    partners: '12,000+',
    instagram: '1.6M'
};

const SectionTag = ({ icon: Icon, children }) => (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-[11px] sm:text-xs font-semibold tracking-wide text-indigo-200 uppercase mb-5">
        <Icon className="w-3.5 h-3.5" />
        {children}
    </div>
);

const StatCard = ({ value, label, icon: Icon }) => (
    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5 sm:p-6">
        <Icon className="w-4 h-4 text-indigo-300 mb-3" />
        <div className="text-2xl sm:text-4xl font-black text-white leading-none mb-2">{value}</div>
        <div className="text-[11px] sm:text-xs text-gray-400 leading-snug">{label}</div>
    </div>
);

/* Bar that animates once, respects reduced motion. Length IS the argument. */
const Bar = ({ label, value, pct, accent }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: '-60px' });
    return (
        <div ref={ref} className="mb-6">
            <div className="flex items-baseline justify-between mb-2 gap-3">
                <span className="text-xs sm:text-sm text-gray-300 leading-snug">{label}</span>
                <span className={`text-sm sm:text-base font-black tabular-nums ${accent ? 'text-indigo-200' : 'text-gray-400'}`}>{value}</span>
            </div>
            <div className="h-2.5 w-full rounded-full bg-white/5 overflow-hidden">
                <motion.div
                    initial={{ width: 0 }}
                    animate={inView ? { width: `${Math.max(pct, 1.2)}%` } : { width: 0 }}
                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                    style={{ minWidth: inView ? 7 : 0 }}
                    className={`h-full rounded-full ${accent ? 'bg-gradient-to-r from-indigo-400 to-indigo-200' : 'bg-white/20'}`}
                />
            </div>
        </div>
    );
};

const Row = ({ ok, children }) => (
    <div className="flex gap-3 items-start py-2.5">
        {ok
            ? <Check className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
            : <X className="w-4 h-4 text-rose-300 mt-0.5 shrink-0" />}
        <span className="text-sm text-gray-300 leading-relaxed">{children}</span>
    </div>
);

export default function SmritiIraniBrief() {
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        document.title = 'Private brief for the office of Smriti Irani';
        const m = document.createElement('meta');
        m.name = 'robots';
        m.content = 'noindex, nofollow';
        document.head.appendChild(m);
        return () => { try { document.head.removeChild(m); } catch (e) { } };
    }, []);

    const forwardNote = `The most watched face on Indian television has ${D.subs} subscribers on her own YouTube channel.
Her one video where she hosts and takes you inside a closed room did ${D.hostViews} views, roughly fourteen times her subscriber count.
Full brief, all public numbers, nothing publishes without written approval: https://socialdeck.socialseo.in/smriti-irani`;

    const copyNote = () => {
        navigator.clipboard?.writeText(forwardNote).then(
            () => { setCopied(true); setTimeout(() => setCopied(false), 2600); },
            () => { }
        );
    };

    return (
        <div className="min-h-screen text-white">

            {/* 0. Confidential bar. Pixel one permission. */}
            <div className="sticky top-0 z-50 border-b border-white/10 bg-[#080B11]/90 backdrop-blur">
                <div className="max-w-5xl mx-auto px-5 py-2.5 flex items-center gap-2.5">
                    <Lock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <p className="text-[10px] sm:text-xs text-gray-400 leading-tight">
                        Prepared for the office of Smriti Irani. Private link, not indexed, not shared.
                    </p>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-5">

                {/* 1. HERO */}
                <section className="pt-14 sm:pt-24 pb-16">
                    <p className="text-[11px] sm:text-xs tracking-wide text-gray-500 mb-6">
                        Private brief · SocialSEO LLP, Mumbai
                    </p>
                    <motion.h1
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="text-[34px] leading-[1.1] sm:text-6xl sm:leading-[1.08] font-black tracking-tight mb-7 bg-clip-text text-transparent bg-gradient-to-br from-white via-indigo-100 to-indigo-400"
                    >
                        India's most watched face<br />on television.<br />
                        <span className="text-gray-500">{D.subs} subscribers<br />on her own channel.</span>
                    </motion.h1>
                    <p className="text-base sm:text-xl text-gray-300 leading-relaxed max-w-2xl mb-10">
                        That gap is the whole argument. Everything below is her own public data, and nothing
                        in this proposal publishes without written approval from her office.
                    </p>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        <StatCard icon={Tv} value={D.trp} label={`Kyunki 2 TRP band, top of the Indian charts, 2026`} />
                        <StatCard icon={Users} value={D.subs} label="Subscribers on her YouTube channel today" />
                        <StatCard icon={Eye} value={D.hostViews} label="Views on the one video where she hosts and takes you inside" />
                        <StatCard icon={Building2} value={D.partners} label="Industry partners in the Alliance she chairs, with no engine on it" />
                    </div>
                    <p className="text-[11px] text-gray-600 mt-5 leading-relaxed">
                        Every number here is public and checkable in one tab. Retrieved {RETRIEVED}. Sources at the bottom.
                    </p>
                </section>

                {/* 2. THE DATA MOMENT */}
                <section className="py-16 border-t border-white/10">
                    <SectionTag icon={Eye}>Her channel already ran the experiment</SectionTag>
                    <h2 className="text-2xl sm:text-4xl font-black mb-3 leading-tight">
                        The audience already picked a format.
                    </h2>
                    <p className="text-gray-400 mb-10 text-sm sm:text-base max-w-2xl">
                        One channel. {D.subs} subscribers. These are the numbers her own uploads produced.
                    </p>

                    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6 sm:p-9">
                        <Bar accent label="She hosts, and takes you inside Rashtrapati Bhavan" value={D.hostViews} pct={100} />
                        <Bar label="She interviews the President of India, 73 minutes" value={D.murmuViews} pct={32} />
                        <Bar label="She writes about being a mother" value={D.motherViews} pct={15} />
                        <Bar label="Party message posts" value={`${D.partyLow} to ${D.partyHigh}`} pct={1.2} />

                        <div className="mt-8 pt-7 border-t border-white/10 grid sm:grid-cols-[auto,1fr] gap-5 sm:gap-8 items-center">
                            <div>
                                <div className="text-4xl sm:text-5xl font-black bg-clip-text text-transparent bg-gradient-to-r from-white to-indigo-300 leading-none">14x</div>
                                <div className="text-[11px] text-gray-500 mt-2 leading-snug max-w-[150px]">
                                    her entire subscriber count, from one video where she opens a door
                                </div>
                            </div>
                            <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
                                This is a verdict on formats, not on people. The best thing on the channel is
                                already the right thing. It has simply never been staffed.
                            </p>
                        </div>
                    </div>
                    <p className="text-[11px] text-gray-600 mt-4">
                        Public view counts on youtube.com/@SmritiIrani, retrieved {RETRIEVED}. Her own YouTube
                        Studio data would confirm or correct this in ten seconds, and that is the first thing we would ask for.
                    </p>
                </section>

                {/* 3. THE UNBUILT LANE */}
                <section className="py-16 border-t border-white/10">
                    <SectionTag icon={Landmark}>The unbuilt lane</SectionTag>
                    <h2 className="text-2xl sm:text-4xl font-black mb-6 leading-tight">
                        She founded a global institution.<br />It has no engine on it.
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6">
                            <p className="text-sm text-gray-300 leading-relaxed">
                                The Alliance for Global Good, Gender Equity and Equality. Founded by her, launched
                                at Davos 2024, anchored by CII, backed by the Gates Foundation and the World
                                Economic Forum. {D.partners} industry partners. She ran the WE Lead Lounge at
                                Davos in January 2026.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-indigo-500/30 bg-indigo-900/10 backdrop-blur p-6">
                            <p className="text-sm text-gray-300 leading-relaxed">
                                Its stated purpose is to move gender equity <span className="text-white font-semibold">from
                                advocacy to execution</span>, and <span className="text-white font-semibold">from intent
                                to implementation</span>. That is already the sharpest positioning in Indian public
                                life on this subject. It exists in press releases and nowhere else.
                            </p>
                        </div>
                    </div>
                </section>

                {/* 4. THE BELIEF */}
                <section className="py-16 border-t border-white/10">
                    <SectionTag icon={FileSignature}>The idea</SectionTag>
                    <blockquote className="text-xl sm:text-3xl font-bold leading-snug text-white mb-7 max-w-3xl">
                        "No scheme, no hashtag and no panel changed an Indian woman's life as fast as a
                        purchase order did."
                    </blockquote>
                    <p className="text-gray-400 text-sm sm:text-base leading-relaxed max-w-2xl mb-4">
                        Women written into company P and L statements and supplier lists, not addressed by
                        awareness campaigns. It is credible from her and from almost nobody else, because she
                        ran the ministry and can rank her own levers honestly.
                    </p>
                    <p className="text-gray-400 text-sm sm:text-base leading-relaxed max-w-2xl">
                        The line she becomes known for: <span className="text-white font-semibold">"Show me the
                        purchase order."</span> It gets used by people in rooms she will never enter, which is
                        the only real test of a line.
                    </p>
                </section>

                {/* 5. THE FORMAT */}
                <section className="py-16 border-t border-white/10">
                    <SectionTag icon={DoorOpen}>The format</SectionTag>
                    <h2 className="text-2xl sm:text-4xl font-black mb-6 leading-tight">She hosts.</h2>
                    <p className="text-gray-300 text-sm sm:text-base leading-relaxed max-w-2xl mb-8">
                        Journalists can conduct the interview but cannot get the access. People with the access
                        publish speeches. She is one of the few who has both, and her channel already shows what
                        happens when she uses it. Two long pieces a month, cut into the week's short film.
                        Prestige documentary, not agency reel.
                    </p>
                    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6 sm:p-8">
                        <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-4">What we would need from her</p>
                        <div className="grid sm:grid-cols-3 gap-5">
                            <div><div className="text-2xl font-black text-white mb-1">1 day</div><div className="text-xs text-gray-400">on camera, per month</div></div>
                            <div><div className="text-2xl font-black text-white mb-1">15 min</div><div className="text-xs text-gray-400">of her office's time, weekly, to approve</div></div>
                            <div><div className="text-2xl font-black text-white mb-1">Nothing else</div><div className="text-xs text-gray-400">the archive and the calendar carry the rest</div></div>
                        </div>
                    </div>
                </section>

                {/* 6. CONTROL */}
                <section className="py-16 border-t border-white/10">
                    <SectionTag icon={ShieldCheck}>Control</SectionTag>
                    <h2 className="text-2xl sm:text-4xl font-black mb-7 leading-tight">
                        Her office holds the pen. Always.
                    </h2>
                    <div className="flex flex-col sm:flex-row gap-2.5 mb-9">
                        {['Draft', 'Office review', 'Written approval', 'Publish'].map((s, i) => (
                            <div key={s} className={`flex-1 rounded-xl border px-4 py-3.5 ${i === 2 ? 'border-indigo-500/40 bg-indigo-900/20' : 'border-white/10 bg-white/5'}`}>
                                <div className="text-[10px] text-gray-500 mb-1">Step {i + 1}</div>
                                <div className={`text-sm font-bold ${i === 2 ? 'text-indigo-200' : 'text-white'}`}>{s}</div>
                            </div>
                        ))}
                    </div>
                    <p className="text-xs text-gray-500 mb-8">Nothing skips step three. Silence is never treated as consent.</p>

                    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6 sm:p-8">
                        <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-3">What we will never do</p>
                        <Row ok={false}>No daily party politics or electoral content on the surfaces we run. That work belongs to the party, and we would keep our surface clean of it.</Row>
                        <Row ok={false}>No comment on a live controversy inside 48 hours, and nothing at all during an election silence period.</Row>
                        <Row ok={false}>No paid promotion of any kind unless ECI MCMC pre-certification and platform political-ad authorisation are both in place first.</Row>
                        <Row ok={false}>No fan pages, no undisclosed amplification, no bought engagement, ever.</Row>
                        <Row ok={false}>No press interviews about this account. We do not discuss who we work with.</Row>
                        <div className="h-px bg-white/10 my-4" />
                        <Row ok>Every asset ships with an adversarial read attached, showing the most damaging honest eight second cut of it and the fix already applied.</Row>
                        <Row ok>NDA before any material moves. First quarter month to month, 30 day exit.</Row>
                    </div>
                </section>

                {/* 7. WHO + PRICE */}
                <section className="py-16 border-t border-white/10">
                    <SectionTag icon={PenLine}>Who we are, and what it costs</SectionTag>
                    <p className="text-sm sm:text-base text-gray-300 leading-relaxed max-w-2xl mb-10">
                        SocialSEO LLP, Mumbai. We produce and publish for a small number of founders and public
                        figures. We are not a PR firm and we do not sell relationships. We build the machine that
                        turns one camera day into a month of published work, and we show our workings.
                        If this is not a fit, one line back and you will not hear from us again.
                    </p>

                    <div className="grid md:grid-cols-3 gap-4">
                        {[
                            { name: 'Archive Engine', price: '5,00,000', per: 'per month', body: 'Existing footage only. 12 short films a month. No shoot days required from her at all.', hi: false },
                            { name: 'The Build', price: '12,00,000', per: 'per month', body: '16 short films and 2 long form pieces monthly, one flagship access film per quarter, the full production and approval system, quarterly commitment ledger.', hi: true },
                            { name: 'Institution', price: '25,00,000', per: 'per month', body: 'Embedded Delhi producer, documentary unit, Hindi and English channels, regional versions, partner acquisition reporting.', hi: false }
                        ].map(t => (
                            <div key={t.name} className={`rounded-2xl border p-6 flex flex-col ${t.hi ? 'border-indigo-500/40 bg-indigo-900/15' : 'border-white/10 bg-white/5'}`}>
                                {t.hi && <div className="text-[10px] uppercase tracking-wide text-indigo-300 mb-2">Recommended</div>}
                                <div className="text-sm font-bold text-white mb-2">{t.name}</div>
                                <div className="text-2xl font-black text-white leading-none">₹{t.price}</div>
                                <div className="text-[11px] text-gray-500 mb-4">{t.per}</div>
                                <p className="text-xs text-gray-400 leading-relaxed">{t.body}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-900/10 p-6">
                        <p className="text-sm text-gray-200 leading-relaxed">
                            <span className="font-bold text-white">Month one is free.</span> We deliver the full
                            first slate at our cost. If her office does not approve it, nothing publishes, nothing
                            is owed, and she keeps every asset we made.
                        </p>
                    </div>
                </section>

                {/* 8. THE ASK */}
                <section className="py-16 border-t border-white/10">
                    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-7 sm:p-10">
                        <h2 className="text-2xl sm:text-3xl font-black mb-3 leading-tight">No call yet.</h2>
                        <p className="text-gray-400 text-sm sm:text-base mb-8 max-w-xl leading-relaxed">
                            Read the nine minute walkthrough first, then decide whether it is worth ten minutes
                            of her time.
                        </p>

                        <a
                            href="mailto:suresh@socialseo.in?subject=Smriti%20Irani%20brief&body=Please%20send%20the%20nine%20minute%20walkthrough."
                            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-indigo-500 hover:bg-indigo-400 transition text-white font-bold text-sm mb-4"
                        >
                            Send me the nine minute walkthrough
                            <ArrowRight className="w-4 h-4" />
                        </a>
                        <p className="text-[11px] text-gray-500 mb-8">
                            One reply from a named person. No mailing list, no follow up sequence, no call unless you ask for one.
                        </p>

                        <div className="h-px bg-white/10 mb-7" />

                        <button
                            onClick={copyNote}
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 transition text-white font-semibold text-sm"
                        >
                            <Copy className="w-4 h-4" />
                            {copied ? 'Copied, ready to paste' : 'Copy a three line note to forward'}
                        </button>
                        <p className="text-[11px] text-gray-500 mt-3">
                            Puts a short summary on your clipboard so you do not have to write one.
                        </p>
                    </div>
                </section>

                {/* SOURCES */}
                <section className="py-12 border-t border-white/10">
                    <p className="text-[11px] uppercase tracking-wide text-gray-600 mb-4">Sources, all public, retrieved {RETRIEVED}</p>
                    <ul className="text-[11px] text-gray-500 space-y-2 leading-relaxed">
                        <li>YouTube subscriber count and per video view counts: youtube.com/@SmritiIrani, channel metadata.</li>
                        <li>Kyunki Saas Bhi Kabhi Bahu Thi 2 TRP standing: Pinkvilla and Free Press Journal TRP reports, 2026.</li>
                        <li>Alliance for Global Good partner count, Davos 2026 WE Lead Lounge: Bollywood Hungama, The Tribune, Business Today Davos coverage, January 2026.</li>
                        <li>Alliance launch, CII anchoring and Gates Foundation support: Press Information Bureau release, Business Today, February 2024.</li>
                        <li>Instagram follower count: page metadata, @smritiiraniofficial.</li>
                    </ul>
                    <p className="text-[11px] text-gray-600 mt-8 leading-relaxed">
                        This page carries no tracking pixels and no analytics. It is not indexed. If you would
                        prefer it taken down, reply once and it is gone the same day.
                    </p>
                </section>

                <div className="h-16" />
            </div>
        </div>
    );
}
